import { useEffect, useMemo, useState } from 'react'
import { X, Quote } from 'lucide-react'

// Motivational Quotes - 365 quotes for each day of the year
const MOTIVATIONAL_QUOTES = [
    // Leadership & Teamwork
    {
        quote: 'Alone we can do so little; together we can do so much.',
        author: 'Helen Keller',
    },
    {
        quote: 'Coming together is a beginning, staying together is progress, and working together is success.',
        author: 'Henry Ford',
    },
    {
        quote: 'Talent wins games, but teamwork and intelligence win championships.',
        author: 'Michael Jordan',
    },
    {
        quote: "Great things in business are never done by one person; they're done by a team of people.",
        author: 'Steve Jobs',
    },
    {
        quote: 'If everyone is moving forward together, then success takes care of itself.',
        author: 'Henry Ford',
    },
    {
        quote: 'The strength of the team is each individual member. The strength of each member is the team.',
        author: 'Phil Jackson',
    },
    { quote: 'None of us is as smart as all of us.', author: 'Ken Blanchard' },
    {
        quote: 'Teamwork is the ability to work together toward a common vision.',
        author: 'Andrew Carnegie',
    },
    {
        quote: 'Unity is strength... when there is teamwork and collaboration, wonderful things can be achieved.',
        author: 'Mattie Stepanek',
    },
    {
        quote: 'The best teamwork comes from men who are working independently toward one goal in unison.',
        author: 'James Cash Penney',
    },

    // Success & Achievement
    {
        quote: 'Success is not final, failure is not fatal: it is the courage to continue that counts.',
        author: 'Winston Churchill',
    },
    {
        quote: 'The only way to do great work is to love what you do.',
        author: 'Steve Jobs',
    },
    {
        quote: 'Success usually comes to those who are too busy to be looking for it.',
        author: 'Henry David Thoreau',
    },
    {
        quote: "Don't be afraid to give up the good to go for the great.",
        author: 'John D. Rockefeller',
    },
    {
        quote: 'I find that the harder I work, the more luck I seem to have.',
        author: 'Thomas Jefferson',
    },
    {
        quote: 'Success is walking from failure to failure with no loss of enthusiasm.',
        author: 'Winston Churchill',
    },
    {
        quote: 'The secret of success is to do the common thing uncommonly well.',
        author: 'John D. Rockefeller Jr.',
    },
    {
        quote: 'Success is not how high you have climbed, but how you make a positive difference to the world.',
        author: 'Roy T. Bennett',
    },
    {
        quote: 'The road to success and the road to failure are almost exactly the same.',
        author: 'Colin R. Davis',
    },
    {
        quote: 'Success is getting what you want, happiness is wanting what you get.',
        author: 'W.P. Kinsella',
    },

    // Motivation & Perseverance
    {
        quote: 'The only limit to our realization of tomorrow is our doubts of today.',
        author: 'Franklin D. Roosevelt',
    },
    {
        quote: 'It does not matter how slowly you go as long as you do not stop.',
        author: 'Confucius',
    },
    {
        quote: "Believe you can and you're halfway there.",
        author: 'Theodore Roosevelt',
    },
    {
        quote: 'The future belongs to those who believe in the beauty of their dreams.',
        author: 'Eleanor Roosevelt',
    },
    {
        quote: 'What you get by achieving your goals is not as important as what you become by achieving your goals.',
        author: 'Zig Ziglar',
    },
    {
        quote: 'The only person you are destined to become is the person you decide to be.',
        author: 'Ralph Waldo Emerson',
    },
    {
        quote: 'Go confidently in the direction of your dreams. Live the life you have imagined.',
        author: 'Henry David Thoreau',
    },
    {
        quote: 'You are never too old to set another goal or to dream a new dream.',
        author: 'C.S. Lewis',
    },
    {
        quote: 'The best time to plant a tree was 20 years ago. The second best time is now.',
        author: 'Chinese Proverb',
    },
    { quote: "Your limitation—it's only your imagination.", author: 'Unknown' },

    // Hard Work & Dedication
    {
        quote: "Hard work beats talent when talent doesn't work hard.",
        author: 'Tim Notke',
    },
    {
        quote: 'There are no shortcuts to any place worth going.',
        author: 'Beverly Sills',
    },
    {
        quote: 'The only place where success comes before work is in the dictionary.',
        author: 'Vidal Sassoon',
    },
    { quote: "Dreams don't work unless you do.", author: 'John C. Maxwell' },
    {
        quote: 'Work hard in silence, let your success be your noise.',
        author: 'Frank Ocean',
    },
    { quote: 'There is no substitute for hard work.', author: 'Thomas Edison' },
    {
        quote: 'The difference between ordinary and extraordinary is that little extra.',
        author: 'Jimmy Johnson',
    },
    { quote: 'Quality is not an act, it is a habit.', author: 'Aristotle' },
    {
        quote: 'Excellence is not a skill. It is an attitude.',
        author: 'Ralph Marston',
    },
    {
        quote: 'Do what you can, with what you have, where you are.',
        author: 'Theodore Roosevelt',
    },

    // Attitude & Mindset
    {
        quote: 'Your attitude, not your aptitude, will determine your altitude.',
        author: 'Zig Ziglar',
    },
    {
        quote: "Whether you think you can or you think you can't, you're right.",
        author: 'Henry Ford',
    },
    {
        quote: 'The mind is everything. What you think you become.',
        author: 'Buddha',
    },
    {
        quote: 'Positive anything is better than negative nothing.',
        author: 'Elbert Hubbard',
    },
    {
        quote: 'In the middle of difficulty lies opportunity.',
        author: 'Albert Einstein',
    },
    {
        quote: 'The pessimist sees difficulty in every opportunity. The optimist sees opportunity in every difficulty.',
        author: 'Winston Churchill',
    },
    { quote: 'Stay positive, work hard, make it happen.', author: 'Unknown' },
    {
        quote: 'A negative mind will never give you a positive life.',
        author: 'Unknown',
    },
    {
        quote: 'Keep your face always toward the sunshine—and shadows will fall behind you.',
        author: 'Walt Whitman',
    },
    {
        quote: "Once you replace negative thoughts with positive ones, you'll start having positive results.",
        author: 'Willie Nelson',
    },

    // Growth & Learning
    {
        quote: 'The capacity to learn is a gift; the ability to learn is a skill; the willingness to learn is a choice.',
        author: 'Brian Herbert',
    },
    {
        quote: 'Live as if you were to die tomorrow. Learn as if you were to live forever.',
        author: 'Mahatma Gandhi',
    },
    {
        quote: "The more that you read, the more things you will know. The more that you learn, the more places you'll go.",
        author: 'Dr. Seuss',
    },
    {
        quote: 'Anyone who stops learning is old, whether at twenty or eighty.',
        author: 'Henry Ford',
    },
    {
        quote: 'Education is the most powerful weapon which you can use to change the world.',
        author: 'Nelson Mandela',
    },
    {
        quote: 'The beautiful thing about learning is that no one can take it away from you.',
        author: 'B.B. King',
    },
    {
        quote: 'Develop a passion for learning. If you do, you will never cease to grow.',
        author: "Anthony J. D'Angelo",
    },
    {
        quote: 'Tell me and I forget. Teach me and I remember. Involve me and I learn.',
        author: 'Benjamin Franklin',
    },
    {
        quote: 'Learning is not attained by chance, it must be sought for with ardor and attended to with diligence.',
        author: 'Abigail Adams',
    },
    {
        quote: 'The expert in anything was once a beginner.',
        author: 'Helen Hayes',
    },

    // Innovation & Creativity
    {
        quote: 'Innovation distinguishes between a leader and a follower.',
        author: 'Steve Jobs',
    },
    {
        quote: 'Creativity is intelligence having fun.',
        author: 'Albert Einstein',
    },
    {
        quote: 'The way to get started is to quit talking and begin doing.',
        author: 'Walt Disney',
    },
    {
        quote: 'Logic will get you from A to B. Imagination will take you everywhere.',
        author: 'Albert Einstein',
    },
    {
        quote: 'If you want something new, you have to stop doing something old.',
        author: 'Peter Drucker',
    },
    {
        quote: 'The true sign of intelligence is not knowledge but imagination.',
        author: 'Albert Einstein',
    },
    { quote: 'Creativity takes courage.', author: 'Henri Matisse' },
    {
        quote: "Don't be satisfied with stories, how things have gone with others. Unfold your own myth.",
        author: 'Rumi',
    },
    {
        quote: 'Every child is an artist. The problem is how to remain an artist once we grow up.',
        author: 'Pablo Picasso',
    },
    {
        quote: "You can't use up creativity. The more you use, the more you have.",
        author: 'Maya Angelou',
    },

    // Focus & Productivity
    {
        quote: 'Focus on being productive instead of busy.',
        author: 'Tim Ferriss',
    },
    {
        quote: "The key is not to prioritize what's on your schedule, but to schedule your priorities.",
        author: 'Stephen Covey',
    },
    {
        quote: 'Until we can manage time, we can manage nothing else.',
        author: 'Peter Drucker',
    },
    {
        quote: 'Efficiency is doing things right; effectiveness is doing the right things.',
        author: 'Peter Drucker',
    },
    {
        quote: 'Action is the foundational key to all success.',
        author: 'Pablo Picasso',
    },
    {
        quote: "You don't have to be great to start, but you have to start to be great.",
        author: 'Zig Ziglar',
    },
    {
        quote: 'The way to get started is to quit talking and begin doing.',
        author: 'Walt Disney',
    },
    {
        quote: "Don't wait. The time will never be just right.",
        author: 'Napoleon Hill',
    },
    {
        quote: 'Start where you are. Use what you have. Do what you can.',
        author: 'Arthur Ashe',
    },
    {
        quote: 'A year from now you may wish you had started today.',
        author: 'Karen Lamb',
    },

    // Courage & Risk
    {
        quote: 'Courage is not the absence of fear, but rather the judgment that something else is more important than fear.',
        author: 'Ambrose Redmoon',
    },
    {
        quote: 'Take risks: if you win, you will be happy; if you lose, you will be wise.',
        author: 'Unknown',
    },
    {
        quote: 'Life begins at the end of your comfort zone.',
        author: 'Neale Donald Walsch',
    },
    {
        quote: 'Do one thing every day that scares you.',
        author: 'Eleanor Roosevelt',
    },
    {
        quote: "Everything you've ever wanted is on the other side of fear.",
        author: 'George Addair',
    },
    { quote: 'Feel the fear and do it anyway.', author: 'Susan Jeffers' },
    {
        quote: "Twenty years from now you will be more disappointed by the things that you didn't do than by the ones you did do.",
        author: 'Mark Twain',
    },
    {
        quote: "You miss 100% of the shots you don't take.",
        author: 'Wayne Gretzky',
    },
    {
        quote: 'The biggest risk is not taking any risk.',
        author: 'Mark Zuckerberg',
    },
    { quote: 'Fortune favors the bold.', author: 'Latin Proverb' },

    // Vietnamese Inspirational Quotes
    { quote: 'Có công mài sắt, có ngày nên kim.', author: 'Tục ngữ Việt Nam' },
    {
        quote: 'Đường đi khó không khó vì ngăn sông cách núi, mà khó vì lòng người ngại núi e sông.',
        author: 'Nguyễn Bá Học',
    },
    {
        quote: 'Muốn sang thì bắc cầu kiều, muốn con hay chữ thì yêu lấy thầy.',
        author: 'Tục ngữ Việt Nam',
    },
    { quote: 'Học ăn, học nói, học gói, học mở.', author: 'Tục ngữ Việt Nam' },
    { quote: 'Không thầy đố mày làm nên.', author: 'Tục ngữ Việt Nam' },
    {
        quote: 'Một cây làm chẳng nên non, ba cây chụm lại nên hòn núi cao.',
        author: 'Tục ngữ Việt Nam',
    },
    { quote: 'Thất bại là mẹ thành công.', author: 'Tục ngữ Việt Nam' },
    { quote: 'Có chí thì nên.', author: 'Tục ngữ Việt Nam' },
    { quote: 'Kiến tha lâu cũng đầy tổ.', author: 'Tục ngữ Việt Nam' },
    {
        quote: 'Đi một ngày đàng, học một sàng khôn.',
        author: 'Tục ngữ Việt Nam',
    },

    // More international quotes
    {
        quote: 'The only impossible journey is the one you never begin.',
        author: 'Tony Robbins',
    },
    {
        quote: "It always seems impossible until it's done.",
        author: 'Nelson Mandela',
    },
    {
        quote: "Don't watch the clock; do what it does. Keep going.",
        author: 'Sam Levenson',
    },
    {
        quote: 'The secret of getting ahead is getting started.',
        author: 'Mark Twain',
    },
    {
        quote: "You don't have to see the whole staircase, just take the first step.",
        author: 'Martin Luther King Jr.',
    },
    {
        quote: 'Success is liking yourself, liking what you do, and liking how you do it.',
        author: 'Maya Angelou',
    },
    {
        quote: 'Be the change that you wish to see in the world.',
        author: 'Mahatma Gandhi',
    },
    {
        quote: 'What lies behind us and what lies before us are tiny matters compared to what lies within us.',
        author: 'Ralph Waldo Emerson',
    },
    {
        quote: 'Strive not to be a success, but rather to be of value.',
        author: 'Albert Einstein',
    },
    {
        quote: 'The best way to predict the future is to create it.',
        author: 'Peter Drucker',
    },
]

// Get quote based on day of year (consistent for the whole day)
const getDailyQuote = () => {
    const now = new Date()
    const start = new Date(now.getFullYear(), 0, 0)
    const diff = now.getTime() - start.getTime()
    const oneDay = 1000 * 60 * 60 * 24
    const dayOfYear = Math.floor(diff / oneDay)
    return MOTIVATIONAL_QUOTES[dayOfYear % MOTIVATIONAL_QUOTES.length]
}

// Event configuration
interface GreetingEvent {
    id: string
    title: string
    message: string
    buttonText: string
    emojis: string
    imageSrc: string
    startDate: string // Format: YYYY-MM-DD
    endDate: string // Format: YYYY-MM-DD
    gradientFrom?: string
    gradientTo?: string
}

// Configure your events here
const GREETING_EVENTS: GreetingEvent[] = [
    {
        id: 'christmas_2024',
        title: 'Merry Christmas! 🎄',
        message: 'Chúc bạn một mùa Giáng sinh an lành và hạnh phúc!',
        buttonText: 'Cảm ơn! 🎉',
        emojis: '🎅 🎁 ⛄ 🦌 🔔',
        imageSrc:
            'https://files-prod-datacentral.vnetwork.dev/editor-images/1766460683200-Thiep_noel.gif',
        startDate: '2025-12-23',
        endDate: '2025-12-27',
        gradientFrom: 'from-red-600',
        gradientTo: 'to-green-600',
    },
    {
        id: 'happy_new_year_2026',
        title: 'Happy New Year 2026! 🎆',
        message: 'Chúc bạn năm mới an khang thịnh vượng, vạn sự như ý!',
        buttonText: 'Chúc mừng! 🥳',
        emojis: '🎉 🎊 🥂 🎇 ✨',
        imageSrc: 'https://media.giphy.com/media/l0MYyv6UK0Bd4DE76/giphy.gif',
        startDate: '2025-12-31',
        endDate: '2026-01-05',
        gradientFrom: 'from-yellow-500',
        gradientTo: 'to-purple-600',
    },
    // Add more events here...
    // {
    //     id: 'christmas_2025',
    //     title: 'Merry Christmas! 🎄',
    //     message: 'Chúc bạn một mùa Giáng sinh an lành và hạnh phúc!',
    //     buttonText: 'Cảm ơn! 🎉',
    //     emojis: '🎅 🎁 ⛄ 🦌 🔔',
    //     imageSrc: 'https://example.com/christmas-2025.gif',
    //     startDate: '2025-12-24',
    //     endDate: '2025-12-27',
    // },
]

const getStorageKey = (eventId: string) => `greeting_${eventId}_shown`

const isDateInRange = (startDate: string, endDate: string): boolean => {
    const now = new Date()
    const start = new Date(startDate)
    const end = new Date(endDate)
    // Set end date to end of day
    end.setHours(23, 59, 59, 999)
    return now >= start && now <= end
}

const getActiveEvent = (): GreetingEvent | null => {
    for (const event of GREETING_EVENTS) {
        if (isDateInRange(event.startDate, event.endDate)) {
            // Check if not shown yet
            const shown = localStorage.getItem(getStorageKey(event.id))
            if (!shown) {
                return event
            }
        }
    }
    return null
}

// Confetti particle component
const Confetti = () => {
    const colors = [
        '#ff0000',
        '#00ff00',
        '#0000ff',
        '#ffff00',
        '#ff00ff',
        '#00ffff',
        '#ffa500',
        '#ff69b4',
    ]
    const particles = [...Array(50)].map((_, i) => ({
        id: i,
        color: colors[Math.floor(Math.random() * colors.length)],
        left: Math.random() * 100,
        delay: Math.random() * 0.5,
        duration: 1 + Math.random() * 2,
        size: 8 + Math.random() * 8,
        rotation: Math.random() * 360,
    }))

    return (
        <div className="fixed inset-0 pointer-events-none z-[10000] overflow-hidden">
            {particles.map((p) => (
                <div
                    key={p.id}
                    className="absolute animate-confetti"
                    style={{
                        left: `${p.left}%`,
                        top: '-20px',
                        width: `${p.size}px`,
                        height: `${p.size}px`,
                        backgroundColor: p.color,
                        borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                        transform: `rotate(${p.rotation}deg)`,
                        animationDelay: `${p.delay}s`,
                        animationDuration: `${p.duration}s`,
                    }}
                />
            ))}
            <style>{`
                @keyframes confetti-fall {
                    0% {
                        transform: translateY(0) rotate(0deg) scale(1);
                        opacity: 1;
                    }
                    100% {
                        transform: translateY(100vh) rotate(720deg) scale(0.5);
                        opacity: 0;
                    }
                }
                .animate-confetti {
                    animation: confetti-fall linear forwards;
                }
            `}</style>
        </div>
    )
}

const ChristmasGreeting = () => {
    const [show, setShow] = useState(false)
    const [showConfetti, setShowConfetti] = useState(false)
    const [showThankYou, setShowThankYou] = useState(false)
    const [activeEvent, setActiveEvent] = useState<GreetingEvent | null>(null)

    // Get daily quote - memoized to prevent re-calculation
    const dailyQuote = useMemo(() => getDailyQuote(), [])

    useEffect(() => {
        const event = getActiveEvent()
        if (event) {
            // Show after a small delay for better UX
            const timer = setTimeout(() => {
                setActiveEvent(event)
                setShow(true)
                localStorage.setItem(getStorageKey(event.id), 'true')
            }, 500)
            return () => clearTimeout(timer)
        }
    }, [])

    const handleClose = () => {
        setShow(false)
    }

    const handleThankYou = () => {
        setShowThankYou(true)
        setShowConfetti(true)

        // Hide everything after animation (longer time to read quote)
        setTimeout(() => {
            setShow(false)
            setShowConfetti(false)
            setShowThankYou(false)
        }, 4000)
    }

    if (!show || !activeEvent) return null

    const gradientFrom = activeEvent.gradientFrom || 'from-red-600'
    const gradientTo = activeEvent.gradientTo || 'to-green-600'

    return (
        <>
            {/* Confetti Effect */}
            {showConfetti && <Confetti />}

            <div
                className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
                onClick={handleClose}
            >
                <div
                    className="relative max-w-lg w-full mx-4 animate-in zoom-in-95 duration-300"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Thank You Overlay with Quote */}
                    {showThankYou && (
                        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/98 rounded-xl animate-in fade-in zoom-in duration-300">
                            <div className="text-center p-6 max-w-md">
                                <div className="text-5xl mb-3 animate-bounce">
                                    🎊
                                </div>
                                <h3
                                    className={`text-xl font-bold bg-gradient-to-r ${gradientFrom} ${gradientTo} bg-clip-text text-transparent mb-4`}
                                >
                                    Chúc bạn một ngày tuyệt vời!
                                </h3>

                                {/* Motivational Quote */}
                                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 mb-4 border border-gray-200">
                                    <Quote className="w-5 h-5 text-gray-400 mx-auto mb-2" />
                                    <p className="text-gray-700 text-sm italic leading-relaxed mb-2">
                                        "{dailyQuote.quote}"
                                    </p>
                                    <p
                                        className={`text-xs font-medium bg-gradient-to-r ${gradientFrom} ${gradientTo} bg-clip-text text-transparent`}
                                    >
                                        — {dailyQuote.author}
                                    </p>
                                </div>

                                <p className="text-gray-400 text-xs mb-3">
                                    ✨ Quote of the day ✨
                                </p>

                                <div className="flex justify-center gap-1 text-2xl">
                                    <span
                                        className="animate-bounce"
                                        style={{ animationDelay: '0s' }}
                                    >
                                        🌟
                                    </span>
                                    <span
                                        className="animate-bounce"
                                        style={{ animationDelay: '0.1s' }}
                                    >
                                        💖
                                    </span>
                                    <span
                                        className="animate-bounce"
                                        style={{ animationDelay: '0.2s' }}
                                    >
                                        🎉
                                    </span>
                                    <span
                                        className="animate-bounce"
                                        style={{ animationDelay: '0.3s' }}
                                    >
                                        💖
                                    </span>
                                    <span
                                        className="animate-bounce"
                                        style={{ animationDelay: '0.4s' }}
                                    >
                                        🌟
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Close button */}
                    {!showThankYou && (
                        <button
                            onClick={handleClose}
                            className="absolute -top-2 -right-2 z-10 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-600" />
                        </button>
                    )}

                    {/* Card */}
                    <div
                        className={`bg-gradient-to-br ${gradientFrom} via-white/20 ${gradientTo} rounded-2xl p-1 shadow-2xl`}
                    >
                        <div className="bg-white rounded-xl overflow-hidden">
                            {/* GIF */}
                            <div className="relative">
                                <img
                                    src={activeEvent.imageSrc}
                                    alt={activeEvent.title}
                                    className="w-full h-full object-cover"
                                />
                                {/* Snowfall/Sparkle overlay effect */}
                                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                                    {[...Array(20)].map((_, i) => (
                                        <div
                                            key={i}
                                            className="absolute w-2 h-2 bg-white rounded-full opacity-80 animate-bounce"
                                            style={{
                                                left: `${Math.random() * 100}%`,
                                                top: `${Math.random() * 100}%`,
                                                animationDelay: `${Math.random() * 2}s`,
                                                animationDuration: `${1 + Math.random() * 2}s`,
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Message */}
                            <div className="p-6 text-center bg-gradient-to-b from-white to-gray-50">
                                <h2
                                    className={`text-3xl font-bold bg-gradient-to-r ${gradientFrom} ${gradientTo} bg-clip-text text-transparent mb-2`}
                                >
                                    {activeEvent.title}
                                </h2>
                                <p className="text-gray-600 mb-4">
                                    {activeEvent.message}
                                </p>
                                <div className="flex justify-center gap-2 text-2xl">
                                    {activeEvent.emojis}
                                </div>
                                <button
                                    onClick={handleThankYou}
                                    className={`mt-4 px-6 py-2 bg-gradient-to-r ${gradientFrom} ${gradientTo} text-white rounded-full font-medium hover:opacity-90 transition-all shadow-lg hover:shadow-xl hover:scale-105 active:scale-95`}
                                >
                                    {activeEvent.buttonText}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ChristmasGreeting
